/**
 * Farm & Acre Co - Paid Add-On Recovery (2026-08-04, born from order #4962)
 *
 * fee-auto-add.js (PDP only) captures every paid add-on group — print fees and
 * matching buttons — and persists the full /cart/add.js payload to localStorage
 * BEFORE attempting delivery. Its in-page delivery already survives navigation
 * (keepalive) and retries once. But order #4962 proved that isn't enough: the
 * companion POST failed on a flaky mobile connection, the customer returned 57
 * minutes later on a fresh page load, and nothing remembered the buttons were
 * still owed. "Matching Buttons: Yes" reached the order; the $24 button line
 * didn't.
 *
 * This file is the second line of defence, loaded SITE-WIDE from theme.liquid
 * (the PDP script can't help once the customer lands anywhere else). On every
 * page load — and again on checkout intent — it replays any persisted add-on
 * group whose items are verifiably missing from the cart:
 *
 *   parent line gone        -> entry dropped (order already placed, or the
 *                              submit never reached the cart — a phantom)
 *   items already in cart   -> entry cleaned up (keepalive landed after all)
 *   items missing           -> re-added individually, verified, then cleaned
 *
 * Every add is diffed against /cart.js first and keyed on (_Order Group,
 * variant id), so replays are idempotent — a POST that landed late is never
 * added twice. The third line of defence is the server-side watchdog
 * (scripts/order-audit/check-paid-addons.mjs), which tags anything that still
 * slips through before it gets packed.
 */

(function () {
  'use strict';

  var QUEUE_KEY = 'fac-addon-queue-v1';
  var LOCK_KEY = 'fac-addon-recovery-lock';
  var FRESH_MS = 20 * 1000;        // leave brand-new entries to the PDP's own delivery flow
  var PHANTOM_MS = 10 * 60 * 1000; // parentless entry older than this = phantom, drop it
  var EXPIRY_MS = 14 * 24 * 60 * 60 * 1000;
  var CHECKOUT_TIMEBOX_MS = 2500;  // never hold a customer back from checkout longer than this

  var debug = (window.CartSharedHelpers && window.CartSharedHelpers.createDebugLogger)
    ? window.CartSharedHelpers.createDebugLogger('[ADDON RECOVERY]')
    : { log: function () {}, warn: function () {}, error: function () {} };

  function loadQueue() {
    try {
      var parsed = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  }

  function saveQueue(entries) {
    try {
      if (entries.length) localStorage.setItem(QUEUE_KEY, JSON.stringify(entries));
      else localStorage.removeItem(QUEUE_KEY);
    } catch (e) { /* storage unavailable — nothing to recover anyway */ }
  }

  // Cross-tab lock so two tabs can't both replay the same entry. Stale locks
  // (crashed tab) expire after 15s.
  function takeLock() {
    try {
      var now = Date.now();
      var held = parseInt(localStorage.getItem(LOCK_KEY) || '0', 10);
      if (held && now - held < 15000) return false;
      localStorage.setItem(LOCK_KEY, String(now));
      return true;
    } catch (e) { return true; }
  }

  function releaseLock() {
    try { localStorage.removeItem(LOCK_KEY); } catch (e) {}
  }

  function isCompanion(line) {
    var p = line.properties || {};
    return p['_Auto-added fee'] === 'true' || p['_Button pair'] === 'true';
  }

  function fetchWithTimeout(url, opts, ms) {
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, ms || 8000);
    return fetch(url, Object.assign({}, opts || {}, { signal: ctrl.signal }))
      .finally(function () { clearTimeout(timer); });
  }

  /**
   * One recovery pass. Returns a promise resolving to the number of items
   * re-added. Safe to call repeatedly — locked, diffed, idempotent.
   */
  async function recoverPass(ignoreFreshness) {
    var queue = loadQueue();
    if (!queue.length) return 0;
    if (!takeLock()) { debug.log('Another tab holds the recovery lock — skipping'); return 0; }

    var added = 0;
    try {
      var cart = await fetchWithTimeout('/cart.js', {}, 8000).then(function (r) { return r.json(); });
      var now = Date.now();
      var keep = [];

      for (var i = 0; i < queue.length; i++) {
        var entry = queue[i];
        if (!entry || !entry.og || !Array.isArray(entry.items) || !entry.items.length) continue;
        if (now - (entry.created || 0) > EXPIRY_MS) { debug.warn('Entry expired, dropping:', entry.og); continue; }

        // A fresh entry (or one with an in-flight keepalive POST) belongs to
        // the PDP flow — touching it now risks a double-add.
        var lastActivity = Math.max(entry.created || 0, entry.lastAttempt || 0);
        if (!ignoreFreshness && now - lastActivity < FRESH_MS) { keep.push(entry); continue; }

        // Parent check: the tag line itself must be in the cart. No parent =
        // either the order was already placed / cart cleared (nothing to owe
        // against) or the submit was validation-blocked and never reached the
        // cart (a phantom). Both mean: never replay, just age it out.
        var og = entry.og;
        var hasParent = cart.items.some(function (line) {
          return (line.properties || {})['_Order Group'] === og && !isCompanion(line);
        });
        if (!hasParent) {
          if (now - (entry.created || 0) > PHANTOM_MS) debug.warn('No parent line for', og, '— dropping entry');
          else keep.push(entry); // parent may still be being added — give it time
          continue;
        }

        var missing = entry.items.filter(function (item) {
          return !cart.items.some(function (line) {
            return String(line.variant_id) === String(item.id) &&
                   (line.properties || {})['_Order Group'] === og;
          });
        });

        if (!missing.length) { debug.log('Entry complete in cart, cleaning up:', og); continue; }

        debug.warn('Replaying', missing.length, 'lost add-on item(s) for', og);
        entry.lastAttempt = now;
        var allDelivered = true;
        for (var j = 0; j < missing.length; j++) {
          try {
            var res = await fetchWithTimeout('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [missing[j]] }),
              keepalive: true
            }, 10000);
            if (res.ok) { added++; } else { allDelivered = false; debug.error('Replay failed', res.status, missing[j].id); }
          } catch (e) { allDelivered = false; debug.error('Replay threw:', e); }
        }
        if (!allDelivered) keep.push(entry); // try again on the next page load
        else debug.log('Entry fully recovered:', og);
      }

      saveQueue(keep);
    } catch (e) {
      debug.error('Recovery pass failed:', e);
    } finally {
      releaseLock();
    }

    if (added > 0 && window.CartSharedHelpers && window.CartSharedHelpers.refreshAllCartSections) {
      try { await window.CartSharedHelpers.refreshAllCartSections(debug); } catch (e) {}
    }
    return added;
  }

  // ── Boot recovery: every page load + bfcache restores ──
  var booted = false;
  function bootRecovery() {
    if (booted) return;
    booted = true;
    setTimeout(function () { recoverPass(false); booted = false; }, 1500);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootRecovery);
  else bootRecovery();
  window.addEventListener('pageshow', function (e) { if (e.persisted) bootRecovery(); });

  // ── Checkout-intent flush: last client-side gate before the order is cut ──
  // Covers the cart page and drawer checkout buttons plus plain /checkout
  // links. Express/dynamic checkout buttons are third-party iframes/components
  // and can't be reliably intercepted — the watchdog covers that path.
  var checkoutBypass = false;
  document.addEventListener('click', function (e) {
    if (checkoutBypass) return;
    var el = e.target && e.target.closest
      ? e.target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]')
      : null;
    if (!el) return;
    if (!loadQueue().length) return;

    e.preventDefault();
    e.stopPropagation();
    debug.warn('Checkout intent with pending add-on entries — flushing first');

    var proceed = function () {
      checkoutBypass = true;
      if (el.tagName === 'A' && el.href) window.location.href = el.href;
      else window.location.href = '/checkout';
    };
    // Time-boxed: recovering what we can beats blocking the sale.
    Promise.race([
      recoverPass(true),
      new Promise(function (r) { setTimeout(r, CHECKOUT_TIMEBOX_MS); })
    ]).then(proceed, proceed);
  }, true);
})();
